import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ASSIGNMENTS } from "@/constants/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, GitBranch, Layers } from "lucide-react";

const TARGET_TYPES = [
  { value: "all", label: "Todos los usuarios" },
  { value: "department", label: "Departamento" },
  { value: "job_title", label: "Cargo" },
  { value: "company", label: "Empresa" },
  { value: "office", label: "Oficina" },
  { value: "user", label: "Usuario específico (UPN/email)" },
];

const TARGET_LABEL = Object.fromEntries(TARGET_TYPES.map((t) => [t.value, t.label]));

export default function Assignments() {
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    template_id: "", target_type: "department", target_value: "", priority: 100,
  });

  const load = useCallback(async () => {
    const [rr, tr] = await Promise.all([api.get("/assignments"), api.get("/templates")]);
    setRules(rr.data);
    setTemplates(tr.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.template_id) {
      toast.error("Seleccione una plantilla");
      return;
    }
    if (form.target_type !== "all" && !form.target_value.trim()) {
      toast.error("Especifique el valor objetivo");
      return;
    }
    try {
      const payload = { ...form };
      if (payload.target_type === "all") payload.target_value = "*";
      await api.post("/assignments", payload);
      toast.success("Regla creada");
      setOpen(false);
      setForm({ template_id: "", target_type: "department", target_value: "", priority: 100 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/assignments/${id}`);
      toast.success("Regla eliminada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const templateNameOf = (id) => templates.find((t) => t.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid={ASSIGNMENTS.list}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">
            Reglas
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Asignaciones</h1>
          <p className="text-sm text-slate-500 mt-1">
            Define cómo se asignan las plantillas por departamento, cargo o usuario.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white transition-colors"
          data-testid={ASSIGNMENTS.addBtn}
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Nueva regla
        </Button>
      </div>

      <Card className="p-4 border-slate-200 shadow-sm bg-orange-50/40">
        <div className="flex items-start gap-3">
          <Layers className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
          <div className="text-xs text-slate-700 leading-relaxed">
            <strong>Prioridad de resolución:</strong> Usuario específico → Cargo → Departamento →
            Oficina → Empresa → Todos. Las asignaciones directas a un usuario siempre prevalecen.
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {rules.map((r) => (
          <Card key={r.id} className="p-4 border-slate-200 shadow-sm flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-orange-100 text-orange-700 flex items-center justify-center">
                <GitBranch className="w-4 h-4" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-sm font-medium text-slate-900">{templateNameOf(r.template_id)}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                  Prioridad {r.priority}
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0 flex items-center gap-3">
              <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-slate-200">
                {TARGET_LABEL[r.target_type] || r.target_type}
              </Badge>
              <div className="text-sm text-slate-700 truncate font-mono">
                {r.target_value === "*" ? "(todos)" : r.target_value}
              </div>
            </div>
            <Button
              size="sm" variant="outline" onClick={() => remove(r.id)}
              className="border-slate-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200"
              data-testid={ASSIGNMENTS.deleteBtn(r.id)}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            </Button>
          </Card>
        ))}
        {rules.length === 0 && (
          <div className="text-sm text-slate-400 text-center py-12 border border-dashed border-slate-200 rounded-md bg-white">
            Sin reglas configuradas.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Nueva regla de asignación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Plantilla</Label>
              <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                <SelectTrigger className="mt-1 focus:ring-orange-500" data-testid={ASSIGNMENTS.templateSelect}>
                  <SelectValue placeholder="Seleccionar plantilla..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Aplicar a</Label>
              <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
                <SelectTrigger className="mt-1 focus:ring-orange-500" data-testid={ASSIGNMENTS.targetTypeSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TARGET_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.target_type !== "all" && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                  Valor objetivo
                </Label>
                <Input
                  value={form.target_value}
                  onChange={(e) => setForm({ ...form, target_value: e.target.value })}
                  placeholder={
                    form.target_type === "department" ? "Ej: Pediatría" :
                    form.target_type === "job_title" ? "Ej: Jefe de Enfermería" :
                    form.target_type === "user" ? "Ej: usuario@dominio.com" :
                    "Valor..."
                  }
                  className="mt-1 focus-visible:ring-orange-500"
                  data-testid={ASSIGNMENTS.targetValueInput}
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Prioridad (menor = mayor prioridad)
              </Label>
              <Input
                type="number" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value || "100", 10) })}
                className="mt-1 focus-visible:ring-orange-500"
                data-testid={ASSIGNMENTS.priorityInput}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-200">Cancelar</Button>
            <Button onClick={create} className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid={ASSIGNMENTS.saveBtn}>
              Crear regla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
